import { createServerFn } from "@tanstack/react-start";
import { z } from "zod";
import { requireSupabaseAuth } from "@/integrations/supabase/auth-middleware";
import { ALL_EVENT_KINDS, FOCUS_LABEL } from "@/lib/event-taxonomy";
import { isEvergreenSource, isStale } from "@/lib/source-quality";

const FOCUS_KEYS = Object.keys(FOCUS_LABEL);
const KIND_SET = new Set<string>(ALL_EVENT_KINDS);

const SELECT =
  "id, contact_id, company_id, event_kind, headline, thesis, suggested_play, evidence, score, score_breakdown, confidence, status, outcome, surfaced_at, acted_at, expires_at, created_at, contacts(id, name, initials, photo_url, is_vip, title, company, location, cadence_days, last_interaction_at), companies(id, name, domain)";

/** Today's ranked opportunities. Capped so the feed stays a morning ritual. */
export const getOpportunityFeed = createServerFn({ method: "GET" })
  .middleware([requireSupabaseAuth])
  .handler(async ({ context }) => {
    const { supabase, userId } = context;
    let { data, error } = await supabase
      .from("opportunities")
      .select(SELECT)
      .eq("user_id", userId)
      .eq("status", "open")
      .gt("expires_at", new Date().toISOString())
      .order("score", { ascending: false })
      .limit(12);
    if (error) throw new Error(error.message);

    // Day one: signals exist but nothing has been promoted yet. Run the
    // deterministic promotion inline so the first feed isn't empty.
    if (!data?.length) {
      try {
        const { supabaseAdmin } = await import("@/integrations/supabase/client.server");
        const { catchUpSynthesis } = await import("./catch-up.server");
        const { promoted } = await catchUpSynthesis(supabaseAdmin, userId);
        if (promoted > 0) {
          const retry = await supabase
            .from("opportunities")
            .select(SELECT)
            .eq("user_id", userId)
            .eq("status", "open")
            .gt("expires_at", new Date().toISOString())
            .order("score", { ascending: false })
            .limit(12);
          if (!retry.error) data = retry.data;
        }
      } catch (err) {
        console.error("[opportunities] catch-up synthesis failed", err);
      }
    }

    const rows = (data ?? []).filter((row: any) => {
      const evidence = Array.isArray(row.evidence) ? row.evidence[0] : null;
      return Boolean(evidence?.published_at) && !isStale(evidence.published_at, 30) && !isEvergreenSource(evidence?.url);
    });
    const unseen = rows.filter((r: any) => !r.surfaced_at).map((r: any) => r.id);
    if (unseen.length) {
      await supabase
        .from("opportunities")
        .update({ surfaced_at: new Date().toISOString() })
        .in("id", unseen);
    }
    return { opportunities: rows };
  });

/** Everything you acted on, and what came of it. */
export const getContactOpportunities = createServerFn({ method: "POST" })
  .middleware([requireSupabaseAuth])
  .inputValidator((input) => z.object({ contactId: z.string().uuid() }).parse(input))
  .handler(async ({ data, context }) => {
    const { supabase, userId } = context;
    const { data: rows, error } = await supabase
      .from("opportunities")
      .select(SELECT)
      .eq("user_id", userId)
      .eq("contact_id", data.contactId)
      .eq("status", "open")
      .gt("expires_at", new Date().toISOString())
      .order("score", { ascending: false })
      .limit(10);
    if (error) throw new Error(error.message);
    const eligible = (rows ?? []).filter((row: any) => {
      const evidence = Array.isArray(row.evidence) ? row.evidence[0] : null;
      return Boolean(evidence?.published_at) && !isStale(evidence.published_at, 30) && !isEvergreenSource(evidence?.url);
    });
    return { opportunities: eligible };
  });

export const getPipeline = createServerFn({ method: "GET" })
  .middleware([requireSupabaseAuth])
  .handler(async ({ context }) => {
    const { supabase, userId } = context;
    const { data, error } = await supabase
      .from("opportunities")
      .select(SELECT)
      .eq("user_id", userId)
      .in("status", ["acted", "won", "closed"])
      .order("acted_at", { ascending: false, nullsFirst: false })
      .limit(60);
    if (error) throw new Error(error.message);

    const rows = data ?? [];
    const counts = { acted: 0, won: 0, closed: 0 };
    for (const r of rows as any[]) {
      if (r.status in counts) counts[r.status as keyof typeof counts]++;
    }
    return { pipeline: rows, counts };
  });

// "open" exists so an undone Skip can put the opportunity back on the board.
const ACTIONS = ["open", "acted", "snoozed", "dismissed", "won", "closed"] as const;

/** Record what you did with an opportunity — this is the ROI ledger. */
export const actOnOpportunity = createServerFn({ method: "POST" })
  .middleware([requireSupabaseAuth])
  .inputValidator((input) =>
    z
      .object({
        opportunityId: z.string().uuid(),
        action: z.enum(ACTIONS),
        snoozeDays: z.number().int().min(1).max(90).optional(),
        outcome: z.string().max(60).optional(),
        note: z.string().max(500).optional(),
      })
      .parse(input),
  )
  .handler(async ({ data, context }) => {
    const { supabase, userId } = context;
    const { data: row, error: readErr } = await supabase
      .from("opportunities")
      .select("id, event_kind")
      .eq("id", data.opportunityId)
      .eq("user_id", userId)
      .maybeSingle();
    if (readErr) throw new Error(readErr.message);
    if (!row) throw new Error("Opportunity not found");

    const now = new Date().toISOString();
    const patch: {
      status: string;
      snoozed_until?: string;
      acted_at?: string;
      outcome?: string;
      outcome_note?: string;
    } = { status: data.action };
    if (data.action === "snoozed") {
      patch.snoozed_until = new Date(
        Date.now() + (data.snoozeDays ?? 7) * 86_400_000,
      ).toISOString();
    }
    if (data.action === "acted" || data.action === "won" || data.action === "closed") {
      patch.acted_at = now;
      if (data.outcome) patch.outcome = data.outcome;
      if (data.note) patch.outcome_note = data.note;
    }

    const { error } = await supabase
      .from("opportunities")
      .update(patch)
      .eq("id", data.opportunityId)
      .eq("user_id", userId);
    if (error) throw new Error(error.message);

    // Feedback tunes future ranking (fatigue on kinds you keep dismissing).
    await supabase.from("opportunity_feedback").insert({
      user_id: userId,
      opportunity_id: data.opportunityId,
      event_kind: row.event_kind,
      action: data.action,
    });

    return { ok: true };
  });

/** What matters to you — drives the per-user event weighting. */

/**
 * One-click relevance feedback on a topic-driven opportunity.
 * Nudges the per-user weight for that event kind so the engine learns,
 * and hides the row when it wasn't relevant.
 */
export const rateOpportunity = createServerFn({ method: "POST" })
  .middleware([requireSupabaseAuth])
  .inputValidator((input) =>
    z
      .object({
        opportunityId: z.string().uuid(),
        relevant: z.boolean(),
      })
      .parse(input),
  )
  .handler(async ({ data, context }) => {
    const { supabase, userId } = context;

    const { data: row, error: readErr } = await supabase
      .from("opportunities")
      .select("id, event_kind, contact_id, evidence")
      .eq("id", data.opportunityId)
      .eq("user_id", userId)
      .maybeSingle();
    if (readErr) throw new Error(readErr.message);
    if (!row) throw new Error("Opportunity not found");

    const kind = row.event_kind as string;

    await supabase.from("opportunity_feedback").insert({
      user_id: userId,
      opportunity_id: data.opportunityId,
      event_kind: kind,
      action: data.relevant ? "relevant" : "not_relevant",
    });

    // Learn: nudge this kind's weight within the same 0–2 range the
    // focus settings use, so repeated feedback compounds gently.
    if (KIND_SET.has(kind)) {
      const { data: profile } = await supabase
        .from("profiles")
        .select("event_weights")
        .eq("id", userId)
        .maybeSingle();
      const weights = ((profile?.event_weights as Record<string, number>) ?? {}) as Record<
        string,
        number
      >;
      const current = typeof weights[kind] === "number" ? weights[kind] : 1;
      const next = Math.min(2, Math.max(0, current + (data.relevant ? 0.15 : -0.2)));
      await supabase
        .from("profiles")
        .update({
          event_weights: { ...weights, [kind]: Number(next.toFixed(2)) },
          updated_at: new Date().toISOString(),
        })
        .eq("id", userId);
    }

    if (!data.relevant) {
      await supabase
        .from("opportunities")
        .update({ status: "dismissed" })
        .eq("id", data.opportunityId)
        .eq("user_id", userId);
    }

    // Topic-level learning: a bad interest match is usually the topic's fault,
    // not the event kind's. Untag the person, and retire topics that keep
    // missing so the same homonym never comes back.
    const topicId = Array.isArray(row.evidence)
      ? ((row.evidence as any[])[0]?.topic_id as string | undefined)
      : undefined;
    if (!data.relevant && topicId) {
      if (row.contact_id) {
        await supabase
          .from("contact_interests")
          .delete()
          .eq("user_id", userId)
          .eq("contact_id", row.contact_id)
          .eq("topic_id", topicId);
      }
      const { data: topic } = await supabase
        .from("interest_topics")
        .select("id, rejection_count")
        .eq("id", topicId)
        .eq("user_id", userId)
        .maybeSingle();
      if (topic) {
        const rejections = (Number(topic.rejection_count) || 0) + 1;
        await supabase
          .from("interest_topics")
          .update({
            rejection_count: rejections,
            // Three strikes: the query itself is wrong, stop spending on it.
            ...(rejections >= 3 ? { is_active: false } : {}),
          })
          .eq("id", topicId)
          .eq("user_id", userId);
      }
    }

    return { ok: true, relevant: data.relevant };
  });


export const getFocusSettings = createServerFn({ method: "GET" })
  .middleware([requireSupabaseAuth])
  .handler(async ({ context }) => {
    const { supabase, userId } = context;
    const { data } = await supabase
      .from("profiles")
      .select("professional_focus, event_weights")
      .eq("id", userId)
      .maybeSingle();
    return {
      focus: (data?.professional_focus as string | null) ?? "general",
      weights: (data?.event_weights as Record<string, number>) ?? {},
      options: Object.entries(FOCUS_LABEL).map(([value, label]) => ({ value, label })),
    };
  });

export const updateFocusSettings = createServerFn({ method: "POST" })
  .middleware([requireSupabaseAuth])
  .inputValidator((input) =>
    z
      .object({
        focus: z.string().refine((f) => FOCUS_KEYS.includes(f), "unknown focus"),
        weights: z
          .record(
            z.string().refine((k) => KIND_SET.has(k), "unknown event kind"),
            z.number().min(0).max(2),
          )
          .optional(),
      })
      .parse(input),
  )
  .handler(async ({ data, context }) => {
    const { supabase, userId } = context;
    const patch: {
      professional_focus: string;
      updated_at: string;
      event_weights?: Record<string, number>;
    } = {
      professional_focus: data.focus,
      updated_at: new Date().toISOString(),
    };
    if (data.weights) patch.event_weights = data.weights;
    const { error } = await supabase.from("profiles").update(patch).eq("id", userId);
    if (error) throw new Error(error.message);
    return { ok: true };
  });
/**
 * Build the feed for the signed-in user right now.
 *
 * Onboarding and bulk add finish long before the nightly synthesis job runs,
 * so without this a brand-new account stares at an empty Today until the next
 * morning. Safe to call repeatedly — promotion and copy writing are both
 * idempotent.
 */
export const synthesizeNow = createServerFn({ method: "POST" })
  .middleware([requireSupabaseAuth])
  .handler(async ({ context }) => {
    const { userId } = context;
    try {
      const { supabaseAdmin } = await import("@/integrations/supabase/client.server");
      const { synthesizeForUserNow } = await import("./catch-up.server");
      return await synthesizeForUserNow(supabaseAdmin, userId);
    } catch (err) {
      console.error("[synthesize-now] failed", err);
      return { promoted: 0, written: 0 };
    }
  });
